import { Link } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CAMINHO_DO_LOGIN } from '@/componentes/Casca';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { formatarFaixa, formatarInteiro, interpretarNumero, unidadeDeConsumo } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { ErroDaApi } from './api';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA } from './busca';
import {
  useCadastrarConfiguracao,
  useCadastrarFabricante,
  useCadastrarModelo,
  useCatalogo,
  useTiposDeEquipamento,
} from './consultas-frota';
import { MensagemDeErro } from './MensagemDeErro';
import { opcoesDeTipo } from './tipos';
import type { Configuracao, Fabricante, Modelo } from './frota';
import { BARRA_DO_CARTAO, CARTAO_DE_FORMULARIO, SecaoDoFormulario } from './SecaoDoFormulario';
import { opcaoDoModelo } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * Ferramenta 1 — fabricante, modelo e unidade. Esta tela cuida dos TRES
 * primeiros niveis; a unidade tem tela propria.
 *
 * ## Por que a configuracao existe, e nao so o modelo
 *
 * O documento v6 diz o que a hierarquia evita: "tratar versoes diferentes como
 * iguais". Duas PC200-8 com cabine e lanca diferentes consomem diferente e usam
 * peca diferente. O nivel que carrega o que a maquina HERDA e a configuracao —
 * medidor, capacidade de tanque e faixa de consumo moram nela, e a unidade so
 * sobrescreve quando tem motivo.
 *
 * ## Um formulario por vez, e um laranja so
 *
 * Sao tres cadastros na mesma tela. Tres botoes primarios competindo violariam
 * a regra 1 do checklist ("uma unica acao em laranja por tela") e, pior,
 * deixariam a pessoa sem saber qual deles e a acao da tela. Entao a tela
 * pergunta O QUE cadastrar e mostra um formulario — com um botao primario, que
 * muda de rotulo junto.
 *
 * ## Um nome so, e um titulo por tabela
 *
 * O lugar se chama "Catálogo" no menu, no botao da frota e no titulo desta
 * tela — eram tres nomes para um lugar. E as tres tabelas vem cada uma sob o
 * proprio titulo de bloco (p.12): so pelo cabecalho das colunas, a primeira e a
 * segunda comecavam ambas por "Fabricante".
 */
type OQue = 'fabricante' | 'modelo' | 'configuracao';

const COLUNAS_FABRICANTE: readonly Coluna<Fabricante>[] = [
  { chave: 'nome', rotulo: 'Fabricante', celula: (f) => f.nome },
];

const COLUNAS_MODELO: readonly Coluna<Modelo>[] = [
  { chave: 'fabricante', rotulo: 'Fabricante', celula: (m) => m.fabricante ?? '—' },
  { chave: 'nome', rotulo: 'Modelo', celula: (m) => m.nome },
  { chave: 'tipo', rotulo: 'Tipo', celula: (m) => m.tipo },
];

const COLUNAS_CONFIGURACAO: readonly Coluna<Configuracao>[] = [
  {
    chave: 'modelo',
    rotulo: 'Modelo',
    celula: (c) => (
      <span className="min-w-0">
        <span className="txt-corpo block text-tinta">{c.modelo ?? '—'}</span>
        <span className="txt-linha2 block text-tinta-secundaria">{c.fabricante ?? 'Sem fabricante'}</span>
      </span>
    ),
  },
  { chave: 'nome', rotulo: 'Configuração', celula: (c) => c.nome },
  {
    chave: 'medidor',
    rotulo: 'Medidor',
    celula: (c) => (c.tipoMedidor === 'odometro' ? 'Odômetro' : 'Horímetro'),
  },
  {
    chave: 'tanque',
    rotulo: 'Tanque',
    alinhamento: 'numero',
    celula: (c) =>
      c.capacidadeTanqueL === null ? (
        <span className="text-tinta-secundaria">—</span>
      ) : (
        <Medida valor={formatarInteiro(c.capacidadeTanqueL)} unidade="L" />
      ),
  },
  {
    chave: 'consumo',
    rotulo: 'Consumo',
    alinhamento: 'numero',
    /* A faixa com as casas que tem e na unidade do MEDIDOR: o FMX 440, de
       odômetro, aparecia "0 a 1 L/h" — arredondado e em litros por hora —, e o
       prontuário da mesma máquina dizia outra coisa. A ponta que falta é "—",
       e não zero. */
    celula: (c) => {
      const faixa = formatarFaixa(c.consumoMinimo, c.consumoMaximo);
      return faixa === null ? (
        <span className="text-tinta-secundaria">—</span>
      ) : (
        <Medida valor={faixa} unidade={unidadeDeConsumo(c.tipoMedidor)} />
      );
    },
  },
];

export function PaginaCatalogo() {
  const catalogo = useCatalogo();
  const [oQue, setOQue] = useState<OQue>('fabricante');
  /* Cadastrar no catálogo só com `catalogo.gerir` (prova do marco, 07/10/2026):
     a Oficina e o Almoxarifado leem o catálogo e não o mudam. */
  const sessao = useSessao();
  const podeGerir = (sessao.data?.permissoes ?? []).includes('catalogo.gerir');
  /* O seletor "O que cadastrar" tem id conhecido: e para ele que os estados
     vazios levam o foco. */
  const idDoSeletor = useId();

  /* O catalogo cresce com a frota: cada tabela tem rodape com contagem e
     paginacao (p.18), local — a lista inteira ja chega numa consulta so. */
  const fabricantes = usePaginaLocal(catalogo.data?.fabricantes ?? []);
  const modelos = usePaginaLocal(catalogo.data?.modelos ?? []);
  const configuracoes = usePaginaLocal(catalogo.data?.configuracoes ?? []);

  const semSessao = catalogo.error instanceof ErroDaApi && catalogo.error.status === 401;

  /** Do estado vazio ao formulario certo, com o foco nele (regra 9: vazio com acao). */
  function irParaOFormulario(novo: OQue) {
    setOQue(novo);
    document.getElementById(idDoSeletor)?.focus();
  }

  /**
   * FALHA NAO E VAZIO.
   *
   * As tres tabelas desta tela recebiam `carregando` e `vazio` e nenhum `erro`.
   * Resultado: backend fora do ar ou 500 — qualquer coisa que nao fosse 401 —
   * caia no ramo normal com `catalogo.data` indefinido, as linhas viravam `[]`
   * e a pessoa lia "Nenhum fabricante cadastrado — cadastre o primeiro" com o
   * catalogo inteiro intacto do outro lado. Era um convite a recadastrar o que
   * ja existe.
   *
   * O mesmo bloco serve as tres tabelas: o erro e um so (uma consulta so), e
   * repetir o texto tres vezes seria tres frases para divergir depois.
   */
  const erroDoCatalogo =
    catalogo.isError && !semSessao ? (
      <EstadoVazio
        titulo="Não foi possível carregar o catálogo"
        descricao="O catálogo não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
        acao={
          <button
            type="button"
            className="botao botao-secundario"
            onClick={() => {
              void catalogo.refetch();
            }}
          >
            Carregar o catálogo
          </button>
        }
      />
    ) : null;

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Frota"
          titulo="Catálogo"
          subtitulo="Fabricantes, modelos e configurações: a hierarquia que evita tratar versões diferentes como iguais"
          acoes={
            <Link to="/equipamentos" search={{}} className="botao botao-secundario">
              Voltar à frota
            </Link>
          }
        />

        {semSessao ? (
          <EstadoVazio
            titulo="Sua sessão não está aberta"
            descricao="Sua sessão não está aberta, ou expirou. Entre outra vez para ver o catálogo."
            acao={
              <Link to={CAMINHO_DO_LOGIN} className="botao botao-secundario">
                Entrar de novo
              </Link>
            }
          />
        ) : (
          <>
            {/* R01 (p.28): o formulário em card, em grade, com a barra de
                ações no rodapé dele. */}
            {podeGerir ? (
            <section aria-label="Cadastrar no catálogo" className={CARTAO_DE_FORMULARIO}>
              <SecaoDoFormulario primeira>
                <CampoSelecao
                  id={idDoSeletor}
                  rotulo="O que cadastrar"
                  valor={oQue}
                  aoMudar={(v) => {
                    setOQue(v === 'modelo' || v === 'configuracao' ? v : 'fabricante');
                  }}
                  opcoes={[
                    { valor: 'fabricante', rotulo: 'Fabricante' },
                    { valor: 'modelo', rotulo: 'Modelo' },
                    { valor: 'configuracao', rotulo: 'Configuração' },
                  ]}
                />
              </SecaoDoFormulario>

              {oQue === 'fabricante' ? <FormularioFabricante /> : null}
              {oQue === 'modelo' ? (
                <FormularioModelo
                  fabricantes={catalogo.data?.fabricantes ?? []}
                  carregando={catalogo.isPending}
                  aoEscolher={irParaOFormulario}
                />
              ) : null}
              {oQue === 'configuracao' ? (
                <FormularioConfiguracao
                  modelos={catalogo.data?.modelos ?? []}
                  carregando={catalogo.isPending}
                  aoEscolher={irParaOFormulario}
                />
              ) : null}
            </section>
            ) : null}

            <BlocoDoCatalogo titulo="Fabricantes">
              <Tabela
                rotulo="Fabricantes cadastrados"
                colunas={COLUNAS_FABRICANTE}
                linhas={fabricantes.visiveis}
                chaveDaLinha={(f) => f.id}
                carregando={catalogo.isPending}
                erro={erroDoCatalogo}
                vazio={
                  <EstadoVazio
                    titulo="Nenhum fabricante cadastrado"
                    descricao="Cadastre o primeiro fabricante para começar a montar o catálogo da frota."
                    acao={
                      podeGerir ? (
                        <Botao
                          nivel="secundario"
                          onClick={() => {
                            irParaOFormulario('fabricante');
                          }}
                        >
                          Cadastrar fabricante
                        </Botao>
                      ) : undefined
                    }
                  />
                }
                rodape={fabricantes.rodape}
              />
            </BlocoDoCatalogo>

            <BlocoDoCatalogo titulo="Modelos">
              <Tabela
                rotulo="Modelos cadastrados"
                colunas={COLUNAS_MODELO}
                linhas={modelos.visiveis}
                chaveDaLinha={(m) => m.id}
                carregando={catalogo.isPending}
                erro={erroDoCatalogo}
                vazio={
                  <EstadoVazio
                    titulo="Nenhum modelo cadastrado"
                    descricao="Cadastre um fabricante e depois o modelo dele para poder criar configurações."
                    acao={
                      podeGerir ? (
                        <Botao
                          nivel="secundario"
                          onClick={() => {
                            irParaOFormulario('modelo');
                          }}
                        >
                          Cadastrar modelo
                        </Botao>
                      ) : undefined
                    }
                  />
                }
                rodape={modelos.rodape}
              />
            </BlocoDoCatalogo>

            <BlocoDoCatalogo titulo="Configurações">
              <Tabela
                rotulo="Configurações cadastradas"
                colunas={COLUNAS_CONFIGURACAO}
                linhas={configuracoes.visiveis}
                chaveDaLinha={(c) => c.id}
                alturaDupla
                carregando={catalogo.isPending}
                erro={erroDoCatalogo}
                vazio={
                  <EstadoVazio
                    titulo="Nenhuma configuração cadastrada"
                    descricao="A configuração é a versão exata da máquina. É dela que cada unidade herda medidor, tanque e faixa de consumo."
                    acao={
                      podeGerir ? (
                        <Botao
                          nivel="secundario"
                          onClick={() => {
                            irParaOFormulario('configuracao');
                          }}
                        >
                          Cadastrar configuração
                        </Botao>
                      ) : undefined
                    }
                  />
                }
                rodape={configuracoes.rodape}
              />
            </BlocoDoCatalogo>
          </>
        )}
      </div>
    </div>
  );
}

/** Um bloco da tela com o proprio titulo (p.12: 21/28, cabecalho de bloco). */
function BlocoDoCatalogo({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }) {
  const id = useId();

  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <h2 id={id} className="txt-secao text-tinta">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/** Paginacao local de uma tabela do catalogo, com o rodape da p.18. */
function usePaginaLocal<T>(linhas: readonly T[]) {
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);

  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const atual = Math.min(pagina, paginas);
  const inicio = (atual - 1) * porPagina;
  const visiveis = linhas.slice(inicio, inicio + porPagina);

  return {
    visiveis,
    rodape: (
      <RodapeTabela
        primeiro={inicio + 1}
        ultimo={inicio + visiveis.length}
        total={linhas.length}
        pagina={atual}
        paginas={paginas}
        porPagina={porPagina}
        opcoesPorPagina={OPCOES_POR_PAGINA}
        aoMudarPagina={setPagina}
        aoMudarPorPagina={(novo) => {
          setPorPagina(novo);
          setPagina(1);
        }}
      />
    ),
  };
}

function FormularioFabricante() {
  const [nome, setNome] = useState('');
  /* Trocar a `rodada` remonta os campos: e o que apaga a mensagem de erro que
     cada um guarda para si quando a pessoa limpa o formulario. */
  const [rodada, setRodada] = useState(0);
  const cadastrar = useCadastrarFabricante();
  const pode = nome.trim().length > 0 && !cadastrar.isPending;

  return (
    <form
      className="flex flex-col"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!pode) return;
        cadastrar.mutate(nome.trim(), { onSuccess: () => { setNome(''); } });
      }}
    >
      <SecaoDoFormulario key={rodada}>
        <div className="campo-largo">
          <CampoTexto
            rotulo="Nome do fabricante"
            valor={nome}
            aoMudar={setNome}
            placeholder="Komatsu"
            validar={(v) => (v.trim() ? null : 'Escreva o nome do fabricante para poder cadastrar.')}
          />
        </div>
      </SecaoDoFormulario>
      {cadastrar.error ? (
        <div className="px-6 pb-6">
          <MensagemDeErro erro={cadastrar.error} />
        </div>
      ) : null}
      {/* Primario a direita e o terciario a esquerda dele (p.15). */}
      <div className={BARRA_DO_CARTAO}>
        <Botao
          nivel="terciario"
          disabled={nome === ''}
          onClick={() => {
            setNome('');
            cadastrar.reset();
            setRodada((atual) => atual + 1);
          }}
        >
          Limpar campos
        </Botao>
        <Botao type="submit" nivel="primario" disabled={!pode}>
          {cadastrar.isPending ? 'Cadastrando' : 'Cadastrar fabricante'}
        </Botao>
      </div>
    </form>
  );
}

function FormularioModelo({
  fabricantes,
  carregando,
  aoEscolher,
}: {
  readonly fabricantes: readonly Fabricante[];
  /** O catalogo ainda nao chegou: a lista vazia NAO quer dizer "sem fabricante". */
  readonly carregando: boolean;
  readonly aoEscolher: (oQue: OQue) => void;
}) {
  const base = useId();
  const [fabricanteId, setFabricanteId] = useState('');
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('');
  const [rodada, setRodada] = useState(0);
  const cadastrar = useCadastrarModelo();
  /* O tipo do modelo sai da LISTA da empresa (0131, decisão 57): o banco
     recusa o que estiver fora dela, e é dele que o código das máquinas nasce. */
  const sessao = useSessao();
  const tipos = useTiposDeEquipamento(sessao.data?.permissoes.includes('equipamento.ler') ?? false);
  const pode = fabricanteId !== '' && nome.trim() !== '' && tipo.trim() !== '' && !cadastrar.isPending;

  if (!carregando && fabricantes.length === 0) {
    return (
      <div className="border-t border-borda-sutil">
        <EstadoVazio
          titulo="Cadastre um fabricante primeiro"
          descricao="O modelo pertence a um fabricante. Cadastre o fabricante e depois volte ao modelo."
          acao={
            <Botao
              nivel="secundario"
              onClick={() => {
                aoEscolher('fabricante');
              }}
            >
              Cadastrar fabricante
            </Botao>
          }
        />
      </div>
    );
  }

  return (
    <form
      className="flex flex-col"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!pode) return;
        cadastrar.mutate(
          { fabricanteId, nome: nome.trim(), tipo: tipo.trim() },
          { onSuccess: () => { setNome(''); setTipo(''); } },
        );
      }}
    >
      <SecaoDoFormulario key={rodada}>
        {/* Os fabricantes ainda vem (p.32): o rotulo fica, e so o controle
            vira a barra de 40 px. O resto do formulario ja pode ser escrito. */}
        {carregando ? (
          <Campo id={`${base}-fabricante`} rotulo="Fabricante">
            <ControleCarregando oQue="Carregando os fabricantes" />
          </Campo>
        ) : (
          <CampoSelecao
            rotulo="Fabricante"
            valor={fabricanteId}
            aoMudar={setFabricanteId}
            rotuloVazio="Escolha o fabricante"
            opcoes={fabricantes.map((f) => ({ valor: f.id, rotulo: f.nome }))}
          />
        )}
        <CampoTexto
          rotulo="Nome do modelo"
          valor={nome}
          aoMudar={setNome}
          placeholder="PC200-8"
          validar={(v) => (v.trim() ? null : 'Escreva o nome do modelo para poder cadastrar.')}
        />
        <div className="campo-largo">
          {tipos.isLoading ? (
            <Campo id={`${base}-tipo`} rotulo="Tipo">
              <ControleCarregando oQue="Carregando os tipos de máquina" />
            </Campo>
          ) : (
            <CampoSelecao
              rotulo="Tipo"
              valor={tipo}
              aoMudar={setTipo}
              rotuloVazio="Escolha o tipo"
              opcoes={opcoesDeTipo(tipos.data ?? [])}
              {...(tipos.isError
                ? { erro: 'Não foi possível carregar a lista de tipos. Recarregue a página para tentar de novo.' }
                : { ajuda: 'Da lista da empresa. É o que aparece na coluna Equipamento da lista de frota, e é dele que o código das máquinas nasce.' })}
            />
          )}
        </div>
      </SecaoDoFormulario>
      {cadastrar.error ? (
        <div className="px-6 pb-6">
          <MensagemDeErro erro={cadastrar.error} />
        </div>
      ) : null}
      <div className={BARRA_DO_CARTAO}>
        <Botao
          nivel="terciario"
          disabled={fabricanteId === '' && nome === '' && tipo === ''}
          onClick={() => {
            setFabricanteId('');
            setNome('');
            setTipo('');
            cadastrar.reset();
            setRodada((atual) => atual + 1);
          }}
        >
          Limpar campos
        </Botao>
        <Botao type="submit" nivel="primario" disabled={!pode}>
          {cadastrar.isPending ? 'Cadastrando' : 'Cadastrar modelo'}
        </Botao>
      </div>
    </form>
  );
}

function FormularioConfiguracao({
  modelos,
  carregando,
  aoEscolher,
}: {
  readonly modelos: readonly Modelo[];
  /** O catalogo ainda nao chegou: a lista vazia NAO quer dizer "sem modelo". */
  readonly carregando: boolean;
  readonly aoEscolher: (oQue: OQue) => void;
}) {
  const [modeloId, setModeloId] = useState('');
  const [nome, setNome] = useState('');
  const [medidor, setMedidor] = useState('horimetro');
  const [capacidade, setCapacidade] = useState('');
  const [minimo, setMinimo] = useState('');
  const [maximo, setMaximo] = useState('');
  const [motor, setMotor] = useState('');
  const [rodada, setRodada] = useState(0);
  const cadastrar = useCadastrarConfiguracao();
  const base = useId();
  /* UM ERRO POR CAMPO DA FAIXA, calculado AO SAIR dele (p.16; R01, p.28), como
     na faixa da unidade: a faixa invertida aparece no campo de onde a pessoa
     acabou de sair. */
  const [erroDoMinimo, setErroDoMinimo] = useState<string | null>(null);
  const [erroDoMaximo, setErroDoMaximo] = useState<string | null>(null);

  const numero = (texto: string) => (texto.trim() === '' ? null : interpretarNumero(texto));

  /* "abc" na capacidade virava `null` no envio: o valor digitado sumia sem
     aviso e a configuracao era gravada sem tanque. Agora o campo diz o que
     fazer, e o envio espera. */
  function erroDaCapacidade(texto: string): string | null {
    if (texto.trim() === '') return null;
    const n = interpretarNumero(texto);
    return n !== null && n > 0 ? null : 'Escreva a capacidade em litros, só com números (ex.: 380).';
  }

  /** Os erros da faixa, dado de qual dos dois campos a pessoa saiu. */
  function errosDaFaixa(saiuDe: 'minimo' | 'maximo' | 'envio') {
    const min = numero(minimo);
    const max = numero(maximo);
    const formato = 'Escreva o consumo só com números (ex.: 12 ou 12,5).';
    const invertida = min !== null && max !== null && min > max;
    return {
      minimo:
        minimo.trim() !== '' && min === null
          ? formato
          : invertida && saiuDe !== 'maximo'
            ? `O mínimo não pode passar do máximo (${maximo.trim()}).`
            : null,
      maximo:
        maximo.trim() !== '' && max === null
          ? formato
          : invertida && saiuDe === 'maximo'
            ? `O máximo não pode ficar abaixo do mínimo (${minimo.trim()}).`
            : null,
    };
  }

  function mostrarErrosDaFaixa(saiuDe: 'minimo' | 'maximo' | 'envio') {
    const erros = errosDaFaixa(saiuDe);
    setErroDoMinimo(erros.minimo);
    setErroDoMaximo(erros.maximo);
  }

  const faixaDoEnvio = errosDaFaixa('envio');
  const semErro =
    erroDaCapacidade(capacidade) === null && faixaDoEnvio.minimo === null && faixaDoEnvio.maximo === null;
  const pode = modeloId !== '' && nome.trim() !== '' && semErro && !cadastrar.isPending;
  const emBranco =
    modeloId === '' &&
    nome === '' &&
    medidor === 'horimetro' &&
    capacidade === '' &&
    minimo === '' &&
    maximo === '' &&
    motor === '';

  if (!carregando && modelos.length === 0) {
    return (
      <div className="border-t border-borda-sutil">
        <EstadoVazio
          titulo="Cadastre um modelo primeiro"
          descricao="A configuração é uma versão de um modelo. Cadastre o modelo e depois volte à configuração."
          acao={
            <Botao
              nivel="secundario"
              onClick={() => {
                aoEscolher('modelo');
              }}
            >
              Cadastrar modelo
            </Botao>
          }
        />
      </div>
    );
  }

  return (
    <form
      className="flex flex-col"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!pode) {
          mostrarErrosDaFaixa('envio');
          return;
        }
        cadastrar.mutate(
          {
            modeloId,
            nome: nome.trim(),
            tipoMedidor: medidor === 'odometro' ? 'odometro' : 'horimetro',
            capacidadeTanqueL: numero(capacidade),
            consumoMinimo: numero(minimo),
            consumoMaximo: numero(maximo),
            potenciaKw: null,
            motorModelo: motor.trim() || null,
          },
          { onSuccess: () => { setNome(''); setCapacidade(''); setMinimo(''); setMaximo(''); setMotor(''); } },
        );
      }}
    >
      <div key={rodada} className="flex flex-col">
        <SecaoDoFormulario titulo="Identificação">
          {/* Os modelos ainda vem (p.32): o rotulo fica, e so o controle vira
              a barra de 40 px. */}
          {carregando ? (
            <Campo id={`${base}-modelo`} rotulo="Modelo">
              <ControleCarregando oQue="Carregando os modelos" />
            </Campo>
          ) : (
            <CampoSelecao
              rotulo="Modelo"
              valor={modeloId}
              aoMudar={setModeloId}
              rotuloVazio="Escolha o modelo"
              opcoes={modelos.map((m) => opcaoDoModelo(m))}
            />
          )}
          <div className="campo-largo">
            <CampoTexto
              rotulo="Nome da configuração"
              valor={nome}
              aoMudar={setNome}
              placeholder="Cabine fechada, lança 6 m"
              ajuda="O que diferencia esta versão das outras do mesmo modelo."
              validar={(v) => (v.trim() ? null : 'Escreva o nome da configuração para poder cadastrar.')}
            />
          </div>
        </SecaoDoFormulario>
        <SecaoDoFormulario titulo="Especificação">
          <CampoSelecao
            rotulo="Medidor"
            valor={medidor}
            aoMudar={setMedidor}
            opcoes={[
              { valor: 'horimetro', rotulo: 'Horímetro' },
              { valor: 'odometro', rotulo: 'Odômetro' },
            ]}
          />
          {/* O MOTOR DE FÁBRICA desta configuração. Cada unidade herda, e só
              preenche o dela quando o motor foi trocado — mesma convenção do
              medidor e da capacidade. */}
          <CampoTexto
            rotulo="Motor"
            opcional
            valor={motor}
            aoMudar={setMotor}
            placeholder="Cummins QSB6.7"
            ajuda="O motor de fábrica desta versão. Cada unidade herda, e pode sobrescrever quando o motor for trocado."
          />
          <CampoTexto
            rotulo="Capacidade do tanque"
            opcional
            valor={capacidade}
            aoMudar={setCapacidade}
            placeholder="380"
            ajuda="Em litros. Cada unidade herda este valor, e pode sobrescrevê-lo."
            validar={erroDaCapacidade}
          />
        </SecaoDoFormulario>
        <SecaoDoFormulario titulo="Consumo esperado">
          {/* A unidade segue o medidor escolhido acima: com odômetro a faixa é
              em litros por quilômetro, e o exemplo de 12 a 22 induziria a
              cadastrar um caminhão com 12 L/km. */}
          <Campo
            id={`${base}-minimo`}
            rotulo="Consumo mínimo"
            opcional
            ajuda={`Em ${medidor === 'odometro' ? 'litros por quilômetro' : 'litros por hora'}. Serve de faixa para conferir o consumo lançado.`}
            erro={erroDoMinimo}
          >
            <input
              id={`${base}-minimo`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="controle w-full"
              placeholder={medidor === 'odometro' ? '0,4' : '12'}
              value={minimo}
              onChange={(e) => {
                setErroDoMinimo(null);
                setMinimo(e.target.value);
              }}
              onBlur={() => {
                mostrarErrosDaFaixa('minimo');
              }}
              {...(erroDoMinimo ? { 'aria-invalid': true, 'aria-describedby': `${base}-minimo-erro` } : {})}
            />
          </Campo>
          <Campo id={`${base}-maximo`} rotulo="Consumo máximo" opcional erro={erroDoMaximo}>
            <input
              id={`${base}-maximo`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="controle w-full"
              placeholder={medidor === 'odometro' ? '0,7' : '22'}
              value={maximo}
              onChange={(e) => {
                setErroDoMaximo(null);
                setMaximo(e.target.value);
              }}
              onBlur={() => {
                mostrarErrosDaFaixa('maximo');
              }}
              {...(erroDoMaximo ? { 'aria-invalid': true, 'aria-describedby': `${base}-maximo-erro` } : {})}
            />
          </Campo>
        </SecaoDoFormulario>
      </div>
      {cadastrar.error ? (
        <div className="px-6 pb-6">
          <MensagemDeErro erro={cadastrar.error} />
        </div>
      ) : null}
      <div className={BARRA_DO_CARTAO}>
        <Botao
          nivel="terciario"
          disabled={emBranco}
          onClick={() => {
            setModeloId('');
            setNome('');
            setMedidor('horimetro');
            setCapacidade('');
            setMinimo('');
            setMaximo('');
            setMotor('');
            setErroDoMinimo(null);
            setErroDoMaximo(null);
            cadastrar.reset();
            setRodada((atual) => atual + 1);
          }}
        >
          Limpar campos
        </Botao>
        <Botao type="submit" nivel="primario" disabled={!pode}>
          {cadastrar.isPending ? 'Cadastrando' : 'Cadastrar configuração'}
        </Botao>
      </div>
    </form>
  );
}
